import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '@ui-kit';
import { LOCAL_INFRA_MODULES, LOCAL_INFRA_ALL_PROFILE, LOCAL_INFRA_ALL_COMMAND } from '../data/localInfra';

/**
 * LocalInfraSection — queue #670 §R670-3.
 *
 * `getting-started` '로컬 인프라' 섹션. 가져다쓴 브로커 모듈(§R670-1 실측) → opt-in gate →
 * 루트 docker-compose.yml compose profile → 로컬 접속값 매핑 표(`data/localInfra.ts` SSOT)를
 * 렌더한다. 순수 정적 콘텐츠(BE 무호출) — 5상태 중 default 만 발생한다(§0 사용 공통 불변).
 */

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-3)',
  borderBottom: '2px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  verticalAlign: 'top',
};

const codeStyle: CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

export function LocalInfraSection() {
  const { t } = useTranslation();
  const copyLabel = t('gettingStartedGuide.localInfra.copyCommand');
  const copiedLabel = t('gettingStartedGuide.quickstart.copied');

  return (
    <section id="gsg-local-infra" aria-labelledby="gsg-local-infra-heading">
      <h2
        id="gsg-local-infra-heading"
        style={{ margin: '0 0 var(--spacing-1) 0', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
      >
        {t('gettingStartedGuide.localInfra.heading')}
      </h2>
      <p style={{ margin: '0 0 var(--spacing-1) 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
        {t('gettingStartedGuide.localInfra.intro')}
      </p>
      <p style={{ margin: '0 0 var(--spacing-4) 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        {t('gettingStartedGuide.localInfra.baseNote')}
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.localInfra.col.module')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.localInfra.col.gate')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.localInfra.col.profile')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.localInfra.col.command')}</th>
              <th scope="col" style={thStyle}>{t('gettingStartedGuide.localInfra.col.connect')}</th>
            </tr>
          </thead>
          <tbody>
            {LOCAL_INFRA_MODULES.map((row) => (
              <tr key={row.profile} data-testid={`local-infra-row-${row.profile}`}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{row.module}</td>
                <td style={tdStyle}><code style={codeStyle}>{row.gateKey}</code></td>
                <td style={tdStyle}><code style={codeStyle}>{row.profile}</code></td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <code style={codeStyle}>{row.command}</code>
                    <CopyButton
                      text={row.command}
                      ariaLabel={`${row.module} ${copyLabel}`}
                      copyLabel={copyLabel}
                      copiedLabel={copiedLabel}
                    />
                  </div>
                </td>
                <td style={tdStyle}>
                  {row.connectionLines.map((line) => (
                    <div key={line}><code style={codeStyle}>{line}</code></div>
                  ))}
                  {row.profile === 'rabbitmq' && (
                    <p style={{ margin: 'var(--spacing-1) 0 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {t('gettingStartedGuide.localInfra.rabbitVhostNote')}
                    </p>
                  )}
                </td>
              </tr>
            ))}
            <tr data-testid={`local-infra-row-${LOCAL_INFRA_ALL_PROFILE}`}>
              <td style={{ ...tdStyle, fontWeight: 600 }} colSpan={3}>
                {t('gettingStartedGuide.localInfra.allBrokers')}
              </td>
              <td style={tdStyle} colSpan={2}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <code style={codeStyle}>{LOCAL_INFRA_ALL_COMMAND}</code>
                  <CopyButton
                    text={LOCAL_INFRA_ALL_COMMAND}
                    ariaLabel={`${t('gettingStartedGuide.localInfra.allBrokers')} ${copyLabel}`}
                    copyLabel={copyLabel}
                    copiedLabel={copiedLabel}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
